<script setup lang="ts">
import { computed } from 'vue';

import { ItemType } from '@/library/message/lib';
import type { BaseMessage, RollbackEvent } from '@/library/message/types';
import PlsText from '@/components/PlsText.vue';
import { SystemEvent } from '@/library/message/enum';
import { replaceUrlsWithLinks, sanitizeHTML } from '@/common/utils';
import { useChatMessageCustomisation } from '../composables/useChatMessageCustomisation';
import { removeMessagePrefix } from '@/common/bot/flow-frameworks/messages';

export interface MessageProps {
  /**
   * The message to render.
   */
  message: BaseMessage;
  /**
   * The previous message/notice in the stack.
   */
  previous?: unknown;
  /**
   * The next message/notice in the stack.
   */
  next?: unknown;

  /**
   * Add the classes of the message body.
   */
  classBody?: string;
  /**
   * Add the classes of the container.
   */
  classContainer?: string;
  /**
   * Add the classes to the avatar part.
   */
  classAvatar?: string;
  /**
   * Determines where actions slot is rendered.
   * 'outside-message' - actions appear below the message bubble (default)
   * 'inside-message' - actions appear inside the message bubble
   */
  actionsPlacement?: 'outside-message' | 'inside-message';
  /**
   * Whether to render the bubble background and padding.
   * When false, background is inherited and padding is removed.
   */
  renderBubble?: boolean;
}
const props = withDefaults(defineProps<MessageProps>(), {
  actionsPlacement: 'outside-message',
  renderBubble: true,
});

interface MessageEvents {
  (e: 'message:input', payload: RollbackEvent): void;
}

const emit = defineEmits<MessageEvents>();

// Customisation
const customisation = useChatMessageCustomisation();

/**
 * Show the message on the left side of the screen if the message is to the user.
 */
const isIncoming = computed(() => props.message.memberType === 'agent');
const messageWithUrls = computed(() => {
  return replaceUrlsWithLinks(props.message.message);
});

const messageContainsUrls = computed(() => {
  return messageWithUrls.value.length !== props.message.message.length;
});

const messageContainsHtml = computed(() => {
  return /<[^>]+>/.test(props.message.message);
});

const incomingMessage = computed(() => {
  const render = messageContainsUrls.value || messageContainsHtml ? 'html' : 'string';
  return { render, message: messageWithUrls.value };
});

// const msgStyle = computed(() => {
//   const { previous, next, message: current } = props;
//   return {
//     /**
//      * If this is the first message in the group. A "GROUP" is determined if the
//      * consecutive messages are from the same user.
//      */
//     top: !previous || !ItemType.isMessage(previous) || previous.memberType !== current.memberType,
//     /**
//      * If this is the last message in the group.
//      */
//     bottom: !next || !ItemType.isMessage(next) || next.memberType !== current.memberType,
//   };
// });

/**
 * Resolve body classed based on the message styles.
 * TODO: maybe we'll need to use it to show only one avatar or something like that
 */
// function bodyStyles() {
//   const { top, bottom } = msgStyle.value;
//   const incoming = isIncoming.value;
//   if (!top && !bottom) return incoming ? 'rounded-sm rounded-r-2xl' : 'rounded-sm rounded-l-2xl';
//   if (top && bottom) return 'rounded-2xl';
//   if (top) return incoming ? 'rounded-2xl rounded-bl-sm' : 'rounded-2xl rounded-br-sm';
//   if (bottom) return incoming ? 'rounded-2xl rounded-tl-sm' : 'rounded-2xl rounded-tr-sm';
//   return '';
// }

const renderMessage = computed(() => {
  const messageWithoutPrefix = removeMessagePrefix(props.message.message);
  const containsHtml = /<[^>]+>/.test(messageWithoutPrefix);
  if (containsHtml) {
    return {
      render: 'html',
      message: sanitizeHTML(messageWithoutPrefix),
    };
  }
  return { render: 'string', message: messageWithoutPrefix };
});

const interactive = computed(
  () =>
    ItemType.isInteractiveMessage(props.message) && {
      p1: props.message.interactiveMessageParts[0],
      p2: props.message.interactiveMessageParts[1],
    }
);

const submitRollback = () => {
  const event: RollbackEvent = {
    type: SystemEvent.USER_ROLLBACK,
    message: props.message,
  };
  emit('message:input', event);
};

// Computed styles for customisation
const chatbotBubbleStyle = computed(() => {
  const textColor = customisation.chatbotBubbleTextColor.value;
  const backgroundColor = props.renderBubble ? customisation.chatbotBubbleBackgroundColor.value : 'inherit';

  return {
    ...(textColor ? { color: textColor } : {}),
    ...(backgroundColor ? { backgroundColor } : {}),
  };
});

const userBubbleStyle = computed(() => {
  const textColor = customisation.userBubbleTextColor.value;
  const backgroundColor = props.renderBubble ? customisation.userBubbleBackgroundColor.value : 'inherit';

  return {
    ...(textColor ? { color: textColor } : {}),
    ...(backgroundColor ? { backgroundColor } : {}),
  };
});

const chatbotBubbleBg = computed(() => customisation.chatbotBubbleBackgroundColor.value);
const userBubbleBg = computed(() => customisation.userBubbleBackgroundColor.value);

const agentAvatarSrc = computed(() => customisation.chatbotIcon.value);
const hasCustomAgentAvatar = computed(() => !!agentAvatarSrc.value);
const showChatbotAvatar = computed(() => true);
const showUserAvatar = computed(() => customisation.userBubbleVisible.value);
const brandPrimaryColor = computed(() => customisation.brandPrimaryColor.value);
</script>

<template>
  <!-- Incoming message: Messages from system/agent -->
  <div v-if="isIncoming" data-message data-agent-message class="message-wrapper">
    <div class="agent-message chat-msg-container">
      <!-- Avatar -->
      <div v-if="showChatbotAvatar" data-avatar-container class="agent-avatar-container">
        <div data-avatar class="agent-avatar">
          <img v-if="hasCustomAgentAvatar" :src="agentAvatarSrc" alt="Chatbot" class="agent-avatar-img" />
          <v-icon
            v-else
            name="MdAccountcircleRound"
            class="agent-avatar-icon"
            :style="brandPrimaryColor ? { color: brandPrimaryColor } : undefined"
          ></v-icon>
        </div>
      </div>

      <!-- Message bubble and actions container -->
      <div class="message-content">
        <template v-if="message.message || !$slots.actions">
          <div
            class="message-bubble-row message-bubble-row--agent"
            :class="{ 'message-bubble-row--has-prepend': $slots.prepend }"
          >
            <div class="message-prepend">
              <slot name="prepend"></slot>
            </div>
            <div
              data-body-container
              class="agent chat-bubble-radius wrapper-chat-msg"
              :class="{ 'with-avatar': showChatbotAvatar, 'no-bubble': !renderBubble }"
              :style="chatbotBubbleStyle"
            >
              <slot>
                <PlsText v-if="renderMessage.render === 'string'">{{ renderMessage.message }}</PlsText>
                <!-- eslint-disable-next-line vue/no-v-text-v-html-on-component -->
                <PlsText v-else v-html="renderMessage.message"></PlsText>
              </slot>
              <div v-if="$slots.actions && actionsPlacement === 'inside-message'" class="message-actions-inside">
                <slot name="actions"></slot>
              </div>
            </div>
          </div>
        </template>

        <!-- Actions outside message bubble (below) -->
        <div v-if="$slots.actions && actionsPlacement === 'outside-message'" class="message-actions">
          <slot name="actions"></slot>
        </div>
      </div>
    </div>
  </div>

  <!-- NOTE: Incoming messages are separate from outgoing ones to make them customizable. If there are some components -->
  <!-- to be shared between them, it's better to replicate them, rather than merge them. -->

  <!-- Outgoing message: Messages from end user -->
  <div v-else data-message data-visitor-message class="message-wrapper theme-font-main">
    <div class="chat-msg-container visitor-message">
      <!-- Avatar -->
      <div v-if="showUserAvatar" data-avatar-container class="visitor-avatar-container">
        <div data-avatar class="visitor-avatar">
          <v-icon name="MdAccountcircleRound" class="visitor-avatar-icon"></v-icon>
        </div>
      </div>

      <!-- Message bubble and actions container -->
      <div class="message-content">
        <div class="message-bubble-row" :class="{ 'message-bubble-row--has-prepend': $slots.prepend }">
          <div class="message-prepend">
            <slot name="prepend"></slot>
          </div>
          <div
            data-body-container
            class="chat-bubble-radius visitor wrapper-chat-msg"
            :class="{ 'with-avatar': showUserAvatar, 'no-bubble': !renderBubble }"
            :style="userBubbleStyle"
          >
            <slot>
              <template v-if="interactive">
                <PlsText>{{ interactive.p1 + ' ' }}</PlsText>
                <PlsText class="interactive-text" @click="submitRollback">{{ interactive.p2 }}</PlsText>
              </template>
              <PlsText v-else>{{ renderMessage.message }}</PlsText>
            </slot>

            <!-- Actions inside message bubble -->
            <div v-if="$slots.actions && actionsPlacement === 'inside-message'" class="message-actions-inside">
              <slot name="actions"></slot>
            </div>
          </div>
        </div>

        <!-- Actions outside message bubble (below) -->
        <div v-if="$slots.actions && actionsPlacement === 'outside-message'" class="message-actions">
          <slot name="actions"></slot>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.message-wrapper {
  margin-bottom: 16px;
}

.chat-msg-container {
  display: flex;
  align-items: flex-start;
  position: relative;
  gap: 9px;

  &.agent-message {
    flex-direction: row;
  }

  &.visitor-message {
    flex-direction: row-reverse;
  }
}

.message-content {
  display: inline-flex;
  align-items: flex-start;
  flex-direction: column;
  min-width: 0;
  gap: 12px;
  width: fit-content;
  max-width: 100%;
}

.message-bubble-row {
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  min-width: 0;

  &.message-bubble-row--has-prepend {
    gap: 8px;
  }

  &.message-bubble-row--agent {
    flex-direction: row-reverse;
  }
}

.message-prepend {
  flex-shrink: 0;
  display: flex;
  align-items: center;
}

.agent-message .message-content {
  max-width: calc(100% - 82px); // Reserve space for both avatars (32px each) + gaps

  a {
    text-decoration: underline;
  }

  .wrapper-chat-msg,
  .message-actions {
    width: 100%;
    max-width: 100%;
    box-sizing: border-box;
  }
}

.visitor-message .message-content {
  max-width: calc(100% - 82px); // Reserve space for both avatars (32px each) + gaps
}

.wrapper-chat-msg {
  overflow-x: hidden;
  overflow-y: visible;
  max-width: 100%;
  min-width: 0;
  padding: 12px;
  position: relative;
  white-space: pre-line;

  &.no-bubble {
    padding: 0;
  }

  &.with-avatar {
    &.agent::before {
      content: '';
      position: absolute;
      left: -5px;
      top: 10px;
      width: 0;
      height: 0;
      border-style: solid;
      border-width: 6px 6px 6px 0;
      border-color: transparent v-bind(chatbotBubbleBg) transparent transparent;
    }

    &.visitor::before {
      content: '';
      position: absolute;
      right: -5px;
      top: 10px;
      width: 0;
      height: 0;
      border-style: solid;
      border-width: 6px 0 6px 6px;
      border-color: transparent transparent transparent v-bind(userBubbleBg);
    }
  }

  :deep(.pls-text) {
    overflow-wrap: anywhere;
    word-break: break-word;
    max-width: 100%;
  }

  :deep(.pls-text ul),
  :deep(.pls-text ol) {
    margin: 0.35em 0;
    padding-left: 1.35em;
  }

  :deep(.pls-text ul) {
    list-style-type: disc;
  }

  :deep(.pls-text ol) {
    list-style-type: decimal;
  }

  :deep(.pls-text li) {
    display: list-item;
    margin: 0.2em 0;
    padding-left: 0.15em;
  }

  :deep(.pls-text li::marker) {
    color: inherit;
  }
}

.article-after-item {
  margin-top: 10px;
}

.icon-left {
  display: inline-flex;
  gap: 4px;
  align-items: center;
}

.restart-text {
  display: block;
  margin-top: 16px;
}

.break-word {
  word-break: break-word;
  word-wrap: break-word;
  overflow-wrap: anywhere;
}

.hint-text {
  font-size: 12px;
}

/* Avatar styling */
.agent-avatar-container,
.visitor-avatar-container {
  flex-shrink: 0;
}

.agent-avatar,
.visitor-avatar {
  width: 32px;
  height: 32px;
  min-width: 32px;
  min-height: 32px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  image-rendering: auto;
  transform: translateZ(0);
  box-sizing: border-box;
}

.agent-avatar-img,
.agent-avatar-icon,
.visitor-avatar-icon {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 32px;
  line-height: 32px;
  -webkit-text-size-adjust: 100%;
  text-size-adjust: 100%;
  image-rendering: auto;
  transform: translateZ(0);
  box-sizing: border-box;
}

.interactive-text {
  text-decoration: underline;
  font-weight: bold;
  cursor: pointer;
}

.message-actions-inside {
  margin-top: 12px;
}
</style>
